import React, { useState } from 'react'
import {
  Box,
  Typography,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Radio,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseIcon from '@mui/icons-material/Close'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import EventIcon from '@mui/icons-material/Event'
import { useTimeSelect } from '../../context/Time'
import { C, pillTriggerSx } from '../../utils/designTokens'

const TimeSelector = ({ variant, compact = false }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const { mode, scheduledFor, selectNow, selectScheduled } = useTimeSelect()

  // Local UI flags derived from context — keep existing variable names so the
  // rest of the file's render code doesn't have to change.
  const selectedTime = mode // 'now' | 'scheduled'
  const scheduledTime = scheduledFor
    ? {
        value: `${scheduledFor.getHours()}:${scheduledFor.getMinutes().toString().padStart(2, '0')}`,
        label: scheduledFor.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      }
    : null

  const open = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelectTime = (timeOption, scheduleValue = null) => {
    if (timeOption === 'now') selectNow()
    else if (scheduleValue?.date) selectScheduled(scheduleValue.date)
    handleClose()
  }

  // Generate time slots for the next few hours
  const generateTimeSlots = () => {
    const slots = []
    const now = new Date()
    const currentHour = now.getHours()
    const currentMinute = now.getMinutes()

    // Round up to next 15-minute slot
    let startMinute = Math.ceil(currentMinute / 15) * 15 + 30 // Start 30 mins from now
    let startHour = currentHour

    if (startMinute >= 60) {
      startMinute -= 60
      startHour += 1
    }

    // Generate 8 time slots
    for (let i = 0; i < 8; i++) {
      const hour = (startHour + Math.floor((startMinute + i * 15) / 60)) % 24
      const minute = (startMinute + i * 15) % 60
      const ampm = hour >= 12 ? 'PM' : 'AM'
      const displayHour = hour % 12 || 12
      const displayMinute = minute.toString().padStart(2, '0')
      const timeString = `${displayHour}:${displayMinute} ${ampm}`

      // Build a real Date for this slot (today, hour:minute). If it's earlier
      // than now (rare edge case at hour rollover) bump to tomorrow.
      const slotDate = new Date(now)
      slotDate.setHours(hour, minute, 0, 0)
      if (slotDate <= now) slotDate.setDate(slotDate.getDate() + 1)

      slots.push({
        value: `${hour}:${displayMinute}`,
        label: timeString,
        date: slotDate,
      })
    }

    return slots
  }

  const timeSlots = generateTimeSlots()

  const getDisplayTime = () => {
    if (selectedTime === 'now') {
      return 'Now'
    }
    return scheduledTime?.label || 'Now'
  }

  return (
    <>
      {/* "pill": the time half of the top bar's "Salem, MA · Now" pill. */}
      {variant === 'pill' ? (
        <Box
          component="button"
          type="button"
          onClick={handleClick}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Delivery time: ${getDisplayTime()}. Change time`}
          sx={{ ...pillTriggerSx, backgroundColor: open ? C.surface2 : 'transparent' }}
        >
          {!compact && <AccessTimeIcon sx={{ color: C.ink, fontSize: 18 }} />}
          <span>{getDisplayTime()}</span>
          {!compact && <ExpandMoreIcon
            sx={{
              color: C.muted,
              fontSize: 18,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />}
        </Box>
      ) : (
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 12px',
          cursor: 'pointer',
          borderRadius: '8px',
          backgroundColor: open ? 'rgba(0,0,0,0.04)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <AccessTimeIcon sx={{ color: '#191919', fontSize: 18 }} />
        <Typography
          sx={{
            color: '#191919',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
        >
          {getDisplayTime()}
        </Typography>
        <ExpandMoreIcon
          sx={{
            color: '#767676',
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            mt: 1,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
              Delivery time
            </Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Deliver Now Option */}
          <ListItem
            button
            onClick={() => handleSelectTime('now')}
            sx={{
              borderRadius: '8px',
              mb: 1,
              backgroundColor: selectedTime === 'now' ? '#FFF5F5' : 'transparent',
              border: selectedTime === 'now' ? '1px solid #CC0000' : '1px solid transparent',
              '&:hover': { backgroundColor: selectedTime === 'now' ? '#FFF5F5' : '#F5F5F5' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <FlashOnIcon sx={{ color: selectedTime === 'now' ? '#CC0000' : '#767676' }} />
            </ListItemIcon>
            <ListItemText
              primary="Deliver now"
              secondary="As soon as possible"
              primaryTypographyProps={{
                sx: { fontWeight: 500, color: selectedTime === 'now' ? '#CC0000' : '#191919' },
              }}
              secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
            />
            <Radio
              checked={selectedTime === 'now'}
              sx={{
                color: '#767676',
                '&.Mui-checked': { color: '#CC0000' },
              }}
            />
          </ListItem>

          <Divider sx={{ my: 1.5 }} />

          {/* Schedule for Later */}
          <Typography sx={{ fontSize: '0.75rem', color: '#767676', mb: 1, fontWeight: 500 }}>
            Schedule for later
          </Typography>

          <List sx={{ maxHeight: 200, overflow: 'auto' }}>
            {timeSlots.map((slot, index) => (
              <ListItem
                key={index}
                button
                onClick={() => handleSelectTime('scheduled', slot)}
                sx={{
                  borderRadius: '8px',
                  py: 1,
                  backgroundColor: scheduledTime?.value === slot.value && selectedTime === 'scheduled' ? '#FFF5F5' : 'transparent',
                  border: scheduledTime?.value === slot.value && selectedTime === 'scheduled' ? '1px solid #CC0000' : '1px solid transparent',
                  '&:hover': { backgroundColor: scheduledTime?.value === slot.value && selectedTime === 'scheduled' ? '#FFF5F5' : '#F5F5F5' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <EventIcon sx={{ color: scheduledTime?.value === slot.value && selectedTime === 'scheduled' ? '#CC0000' : '#767676', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={`Today, ${slot.label}`}
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      color: scheduledTime?.value === slot.value && selectedTime === 'scheduled' ? '#CC0000' : '#191919',
                    },
                  }}
                />
                <Radio
                  checked={scheduledTime?.value === slot.value && selectedTime === 'scheduled'}
                  sx={{
                    color: '#767676',
                    '&.Mui-checked': { color: '#CC0000' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </>
  )
}

export default TimeSelector
